import { Heading, Tag } from "@carbon/react";
import { FC } from "react";

import { PassiveModal, ModalTransition } from "src/components/Modal";
import { RelatedWidgetConfiguration } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import RulesWidget from "src/dynamic-view-renderer/Widgets/RulesWidget/RulesWidget.tsx";
import { useFormatDate, useT } from "src/internationalization";
import { ScenarioExecutionResultWithCase } from "src/types/scenario";

type CompareExecutionsModalProps = {
  firstExecution: ScenarioExecutionResultWithCase;
  secondExecution: ScenarioExecutionResultWithCase;
  subtitle?: string;
  onClose: () => void;
};

type ExecutionColumnProps = {
  execution: ScenarioExecutionResultWithCase;
};

const ExecutionColumn: FC<ExecutionColumnProps> = ({ execution }) => {
  const t = useT();
  const formatDate = useFormatDate();
  const scenarioVersion = execution.scenarioVersion;
  const scenarioName = scenarioVersion?.scenario?.name || "";
  const executionDate = formatDate(execution.date, {
    dateStyle: "long",
    timeStyle: "short",
  });
  const globalScore = execution.globalScore;

  const configuration: RelatedWidgetConfiguration = {
    caseColumns: [],
    matchElementsColumns: [],
  };

  const customHeader = (
    <div className="mb-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-2">
          <Heading className="text-lg font-semibold">{scenarioName}</Heading>
          <div className="text-sm text-textSecondary">{executionDate}</div>
        </div>
        <div className="flex items-center gap-2">
          <Tag type="red" className="whitespace-nowrap">
            {t("component.widget.rules.score")} : {globalScore}
          </Tag>
        </div>
      </div>
    </div>
  );

  const widgetDefinition = undefined;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <RulesWidget
          data={{
            scenarioExecutionResult: execution,
          }}
          configuration={configuration}
          customHeader={customHeader}
          widgetDefinition={widgetDefinition}
          widgetConfig={{ widgetCode: "rules" }}
          rowConfiguration={{ widgets: [{ widgetCode: "rules" }] }}
        />
      </div>
    </div>
  );
};

export const CompareExecutionsModal: FC<CompareExecutionsModalProps> = ({
  firstExecution,
  secondExecution,
  subtitle,
  onClose,
}) => {
  const t = useT();

  return (
    <ModalTransition>
      <PassiveModal
        size="xl"
        title={t("page.case.tab.alerts.and.executions.modal.compare.executions.title")}
        subtitle={subtitle}
        onClose={onClose}
      >
        <div className="flex gap-4">
          <div className="flex-1 flex flex-col overflow-hidden border-r border-solid border-borderSubtle02 pr-4">
            <ExecutionColumn execution={secondExecution} />
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            <ExecutionColumn execution={firstExecution} />
          </div>
        </div>
      </PassiveModal>
    </ModalTransition>
  );
};
